import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Scan,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
} from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import { useQRCodeStats } from '../../services/qrCodeService';
import { formatPrice } from '../../lib/utils';

/**
 * QR Code Statistics Drawer — Shows detailed analytics for a QR code
 */
export function QRCodeStatsDrawer({ qrCode, isOpen, onClose }) {
  const { data: stats, isLoading } = useQRCodeStats(qrCode?.id);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showTrackingUrl, setShowTrackingUrl] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!qrCode) return null;

  const trackingUrl = `${process.env.REACT_APP_SUPABASE_URL.replace('/rest/v1', '')}/functions/v1/track-qr-scan?code=${qrCode.slug}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Chart data
  const scansChartData = stats?.scansByDate
    ? {
        labels: stats.scansByDate.map((d) =>
          new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        ),
        datasets: [
          {
            label: 'Scans',
            data: stats.scansByDate.map((d) => d.count),
            borderColor: '#D32323',
            backgroundColor: 'rgba(211,35,35,0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointBackgroundColor: '#D32323',
            pointRadius: 3,
          },
        ],
      }
    : null;

  const ordersChartData = stats?.ordersByDate
    ? {
        labels: stats.ordersByDate.map((d) =>
          new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        ),
        datasets: [
          {
            label: 'Orders',
            data: stats.ordersByDate.map((d) => d.count),
            backgroundColor: 'rgba(34,197,94,0.7)',
            borderRadius: 6,
            maxBarThickness: 40,
          },
        ],
      }
    : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F1F1F',
        cornerRadius: 8,
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#9CA3AF' },
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false },
        ticks: { font: { size: 10 }, color: '#9CA3AF', stepSize: 1, beginAtZero: true },
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-brand-dark/60 backdrop-blur-sm z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] lg:w-[560px] bg-brand-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-ui-border px-4 sm:px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  <h2 className="text-lg font-bold text-brand-dark truncate">{qrCode.name}</h2>
                  <p className="text-xs text-brand-gray mt-0.5">QR Code Analytics</p>
                </div>
                <button
                  onClick={onClose}
                  className="flex-shrink-0 p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Collapsible QR Code Preview */}
              <div className="mt-4">
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="w-full flex items-center justify-between p-3 bg-brand-light rounded-lg hover:bg-ui-border transition-colors"
                >
                  <span className="text-sm font-bold text-brand-dark">QR Code Preview</span>
                  {showQRCode ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                
                <AnimatePresence>
                  {showQRCode && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 p-4 sm:p-6 bg-brand-white border border-ui-border rounded-xl flex items-center justify-center">
                        <div className="w-full max-w-[200px] sm:max-w-[240px] aspect-square">
                          <div
                            dangerouslySetInnerHTML={{ __html: qrCode.qr_data }}
                            className="qr-code-container"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Collapsible Tracking URL */}
              <div className="mt-3">
                <button
                  onClick={() => setShowTrackingUrl(!showTrackingUrl)}
                  className="w-full flex items-center justify-between p-3 bg-brand-light rounded-lg hover:bg-ui-border transition-colors"
                >
                  <span className="text-sm font-bold text-brand-dark">Tracking URL</span>
                  {showTrackingUrl ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                
                <AnimatePresence>
                  {showTrackingUrl && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 p-3 bg-brand-light rounded-lg">
                        <div className="flex items-start gap-2">
                          <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-xs text-brand-red hover:underline break-all"
                          >
                            {trackingUrl}
                          </a>
                          <button
                            onClick={handleCopy}
                            className="flex-shrink-0 p-1.5 rounded hover:bg-brand-white transition-colors"
                            title="Copy URL"
                          >
                            {copied ? (
                              <Check size={14} className="text-success" />
                            ) : (
                              <Copy size={14} className="text-brand-gray" />
                            )}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-brand-red" />
                </div>
              ) : (
                <>
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 sm:p-4 bg-red-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-brand-red/10 flex items-center justify-center">
                          <Scan size={14} className="text-brand-red sm:w-4 sm:h-4" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-brand-gray">Total Scans</p>
                      </div>
                      <p className="text-xl sm:text-2xl font-extrabold text-brand-dark">{stats?.totalScans || 0}</p>
                    </div>

                    <div className="p-3 sm:p-4 bg-green-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-green-600/10 flex items-center justify-center">
                          <ShoppingCart size={14} className="text-green-600 sm:w-4 sm:h-4" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-brand-gray">Orders</p>
                      </div>
                      <p className="text-xl sm:text-2xl font-extrabold text-brand-dark">{stats?.totalOrders || 0}</p>
                    </div>

                    <div className="p-3 sm:p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-600/10 flex items-center justify-center">
                          <TrendingUp size={14} className="text-blue-600 sm:w-4 sm:h-4" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-brand-gray">Conversion</p>
                      </div>
                      <p className="text-xl sm:text-2xl font-extrabold text-brand-dark">
                        {stats?.conversionRate?.toFixed(1) || 0}%
                      </p>
                    </div>

                    <div className="p-3 sm:p-4 bg-amber-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-amber-600/10 flex items-center justify-center">
                          <DollarSign size={14} className="text-amber-600 sm:w-4 sm:h-4" />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-brand-gray">Revenue</p>
                      </div>
                      <p className="text-base sm:text-xl font-extrabold text-brand-dark">
                        {formatPrice(stats?.totalRevenue || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Scans Chart */}
                  <div className="bg-brand-white border border-ui-border rounded-xl p-4">
                    <h3 className="text-sm font-bold text-brand-dark mb-3">Scans (Last 7 Days)</h3>
                    <div className="h-48">
                      {scansChartData ? (
                        <Line data={scansChartData} options={chartOptions} />
                      ) : (
                        <div className="h-full bg-brand-light/50 rounded-xl flex items-center justify-center">
                          <p className="text-sm text-brand-gray">No scan data</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Orders Chart */}
                  <div className="bg-brand-white border border-ui-border rounded-xl p-4">
                    <h3 className="text-sm font-bold text-brand-dark mb-3">Orders (Last 7 Days)</h3>
                    <div className="h-48">
                      {ordersChartData ? (
                        <Bar data={ordersChartData} options={chartOptions} />
                      ) : (
                        <div className="h-full bg-brand-light/50 rounded-xl flex items-center justify-center">
                          <p className="text-sm text-brand-gray">No order data</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recent Orders */}
                  <div className="bg-brand-white border border-ui-border rounded-xl p-4">
                    <h3 className="text-sm font-bold text-brand-dark mb-3">Recent Orders</h3>
                    {!stats?.recentOrders?.length ? (
                      <div className="py-8 text-center">
                        <ShoppingCart size={24} className="text-ui-muted mx-auto mb-2" />
                        <p className="text-sm text-brand-gray">No orders yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {stats.recentOrders.map((order) => (
                          <div
                            key={order.id}
                            className="flex items-center gap-3 p-3 bg-brand-light rounded-lg"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-brand-dark truncate">
                                {order.customer_name}
                              </p>
                              <p className="text-xs text-brand-gray mt-0.5">
                                {order.product_snapshot?.title || 'Product'} × {order.quantity}
                              </p>
                              <p className="text-[10px] text-ui-muted mt-0.5 flex items-center gap-1">
                                <Clock size={10} />
                                {new Date(order.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-extrabold text-brand-dark">
                                {formatPrice(order.total_price)}
                              </p>
                              <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-success/10 text-success mt-0.5">
                                {order.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recent Scans */}
                  <div className="bg-brand-white border border-ui-border rounded-xl p-4">
                    <h3 className="text-sm font-bold text-brand-dark mb-3">Recent Scans</h3>
                    {!stats?.recentScans?.length ? (
                      <div className="py-8 text-center">
                        <Scan size={24} className="text-ui-muted mx-auto mb-2" />
                        <p className="text-sm text-brand-gray">No scans yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {stats.recentScans.map((scan) => (
                          <div
                            key={scan.id}
                            className="flex items-center gap-3 p-3 bg-brand-light rounded-lg"
                          >
                            <div className="w-8 h-8 rounded-lg bg-brand-red/10 flex items-center justify-center flex-shrink-0">
                              <Scan size={14} className="text-brand-red" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-brand-dark truncate">
                                {scan.ip_address || 'Unknown'}
                              </p>
                              <p className="text-[10px] text-brand-gray mt-0.5 flex items-center gap-1">
                                <Clock size={10} />
                                {new Date(scan.scanned_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
